import { Popover } from "@mui/material";

import type { FilterDefinition } from "@/shared/components/filters/FilterDefinition";
import type { Filter } from "@/shared/components/filters/FilterSchema";

import styles from "./EditFilterMenu.module.css";
import { FilterForm } from "./FilterForm";
import { MetricFilterForm } from "./MetricFilterForm";
import { usePopoverRefit } from "./usePopoverRefit";

export interface EditFilterMenuProps {
  /** Whether the popover is currently open.*/
  open: boolean;
  /** Element the popover anchors to (the clicked chip body). */
  anchorEl: HTMLElement | null;
  /** Called on outside click, Escape, Cancel, or after a commit. */
  onClose: () => void;
  /**
   * The committed filter being edited; seeds the form's comparator and
   * values.
   */
  filter: Filter;
  /**
   * Definition backing `filter.field`, for the form header and per-field
   * comparator/options metadata. Unused for `metric` filters (the metric
   * form derives its header from the dotted path). The caller gates the
   * edit affordance on having resolved one, so a non-metric filter always
   * arrives with a definition.
   */
  definition?: FilterDefinition;
  /** Fired with the updated filter when the user submits. `onClose`
   *  fires immediately after. */
  onCommit: (filter: Filter) => void;
}

/**
 * Anchored popover for editing one committed filter in place — the
 * chip-edit counterpart to `<FilterMenu>`.
 */
export function EditFilterMenu({
  open,
  anchorEl,
  onClose,
  filter,
  definition,
  onCommit,
}: EditFilterMenuProps) {
  const refit = usePopoverRefit(anchorEl);
  const commit = (next: Filter) => {
    onCommit(next);
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      action={refit.action}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: {
          ref: refit.paperRef,
          className: styles.popover,
          role: "dialog",
          "aria-label": "Edit filter",
          // Disable MUI's default elevation shadow so the CSS module's
          // box-shadow has sole control over the frame depth.
          elevation: 0,
        },
      }}
    >
      {filter.type === "metric" ? (
        <MetricFilterForm
          metric={{ name: filter.field, unit: filter.unit ?? null }}
          initialFilter={filter}
          submitLabel="Update filter"
          onCommit={commit}
          onCancel={onClose}
        />
      ) : (
        definition && (
          <FilterForm
            selectedField={definition}
            initialFilter={filter}
            submitLabel="Update filter"
            onCommit={commit}
            onCancel={onClose}
          />
        )
      )}
    </Popover>
  );
}
